import React, { useEffect, useRef, useState } from 'react';
import { saveMiningState, loadMiningState } from '../utils/storage';

/**
 * usePersistence Hook - Auto-save state to localStorage
 *
 * Automatically persists state changes to localStorage with:
 * - Debouncing to avoid excessive writes
 * - Only saves when state actually changes
 * - Loads initial state on mount
 *
 * This hook is independent and can be easily disabled or modified.
 */
export function usePersistence(state, setState, defaultState, options = {}) {
    const {
        enabled = true,
        debounceMs = 1000,
        saveOnUnmount = true,
    } = options;

    const saveTimeoutRef = useRef(null);
    const isInitialMount = useRef(true);

    /**
     * Load initial state from storage
     */
    useEffect(() => {
        if (!enabled) return;

        const loadedState = loadMiningState(null);
        if (loadedState) {
            console.log('Loaded state from localStorage');
            setState(loadedState);
        }

        isInitialMount.current = false;
    }, []);

    /**
     * Save state to storage (debounced)
     */
    useEffect(() => {
        if (!enabled || isInitialMount.current) return;

        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Set new timeout
        saveTimeoutRef.current = setTimeout(() => {
            saveMiningState(state);
            console.log('Saved state to localStorage');
        }, debounceMs);

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [state, enabled, debounceMs]);

    /**
     * Save on unmount
     */
    useEffect(() => {
        if (!enabled || !saveOnUnmount) return;

        return () => {
            saveMiningState(state);
            console.log('Saved state on unmount');
        };
    }, [state, enabled, saveOnUnmount]);
}

/**
 * Hook to manage simple localStorage value
 *
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value
 * @returns {[any, Function]} [value, setValue]
 */
export function useLocalStorage(key, defaultValue) {
    const [value, setValue] = React.useState(() => {
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch (error) {
            console.error(`Error loading ${key} from localStorage:`, error);
            return defaultValue;
        }
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.error(`Error saving ${key} to localStorage:`, error);
        }
    }, [key, value]);

    return [value, setValue];
}
